import React, { useState, useEffect } from "react";
import {
  Card,
  TextField,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CardContent,
  Grid,
  Skeleton,
  Box,
  Chip,
  Divider,
  Button as MuiButton,
  Checkbox,
} from "@mui/material";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Table,
} from "reactstrap"; // --- ADDED reactstrap
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCertificate } from "@fortawesome/free-solid-svg-icons";
import Pagination from "@mui/material/Pagination";
import { faSearch } from "@fortawesome/pro-duotone-svg-icons";
import { size, isEmpty } from "lodash";
import AnnouncementDetail from "../../GeneralAvailability/Components/Announcement/AnnouncementDetail";
import { post } from "../../../_helper/api_helper";
import { advanceSearch } from "../../../_helper/services/advanceSearchv2";
import { useNavigate } from "react-router-dom"; // --- ADDED
import { ShowError, ShowSucess } from "../../../_helper/helper";
import { getFormalApiErrorMessage } from "../../../utils/apiError";
// import { ShowSucess, ShowError } from "../../../utils/toast-message"; // --- ADDED (assuming path)

// --- MODIFIED: Renamed prop to 'annId' for clarity, removed 'gaId'
const ViewGA = ({ annId, gaId, setSubstepes = null }) => {
  // Added setSubstepes to navigate
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [data, setData] = useState([]); // This is our 'dataState'
  const [totalItems, setTotalItems] = useState(0);
  const [dataLoader, setDataLoader] = useState(false);
  const [toggleApi, setToggleApi] = useState(false);

  // --- ADDED: State for selection ---
  const [selectedState, setSelectedState] = useState({}); // This tracks selected GAs

  // --- ADDED: State for Modal ---
  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState([]);
  const [submitLoader, setSubmitLoader] = useState(false);
  // Additional applicants (beyond the vacancy count) to include on the
  // certification list, entered by the user at generation time.
  const [additionalApplicantsCount, setAdditionalApplicantsCount] = useState(0);
  const toggleModal = () => setModal(!modal);

  // --- ADDED: Notified applicants list + checkbox selection for the
  // "Create Certification List" modal ---
  const [notifiedApplicants, setNotifiedApplicants] = useState([]);
  const [notifiedApplicantsLoading, setNotifiedApplicantsLoading] = useState(false);
  const [selectedApplicantIds, setSelectedApplicantIds] = useState({});

  const fetchNotifiedApplicants = async () => {
    if (!gaId) return;
    setNotifiedApplicantsLoading(true);
    try {
      const payload = {
        tableName: "GENERAL-AVAIL",
        searchParams: `[{"columnName":"GENERAL_AVAILIBILITY_ID","value":"${gaId}","operator":"=","dataType":"varchar(MAX)","logicOperator":"AND"}]`,
        columns: "*",
        location: "pmis-global",
        pageNumber: 1,
        pageSize: 500,
        sortData: "",
        additionalWhere: "",
        qID: 0,
      };
      const response = await advanceSearch(payload);
      const sortedApplicants = [...(response?.results ?? [])].sort(
        (a, b) =>
          (Number(b?.EVALUATION_RATING) || 0) -
          (Number(a?.EVALUATION_RATING) || 0)
      );
      setNotifiedApplicants(sortedApplicants);
    } catch (error) {
      ShowError(
        getFormalApiErrorMessage(
          error,
          "We are unable to load notified applicants at this time."
        )
      );
      setNotifiedApplicants([]);
    } finally {
      setNotifiedApplicantsLoading(false);
    }
  };

  const toggleApplicantSelection = (applicationId) => {
    setSelectedApplicantIds((prev) => ({
      ...prev,
      [applicationId]: !prev[applicationId],
    }));
  };

  const toggleAllApplicantsSelection = () => {
    const allSelected =
      notifiedApplicants.length > 0 &&
      notifiedApplicants.every(
        (applicant) => selectedApplicantIds[applicant.APPLICATION_ID]
      );
    if (allSelected) {
      setSelectedApplicantIds({});
    } else {
      const next = {};
      notifiedApplicants.forEach((applicant) => {
        next[applicant.APPLICATION_ID] = true;
      });
      setSelectedApplicantIds(next);
    }
  };

  const selectedApplicationIdsString = Object.keys(selectedApplicantIds)
    .filter((applicationId) => selectedApplicantIds[applicationId])
    .map((applicationId) => applicationId.trim())
    .join(",");

  const selectedApplicantCount = Object.values(selectedApplicantIds).filter(
    Boolean
  ).length;

  const history = useNavigate(); // --- ADDED

  const fetchData = async (
    pageNumber = 1,
    pageSize = itemsPerPage,
    searchTerm = ""
  ) => {
    // --- MODIFIED: Don't fetch if annId isn't ready ---
    if (!annId || !gaId) return;

    const url = `${process.env.REACT_APP_API_DOTNET_URL}/PMISAdvanceSearch/pmis-global?pageNumber=${pageNumber}&pageSize=${pageSize}`; // --- MODIFIED: Fetches all GAs for the annId, like GAList ---

    // The gaId prop was removed as it contradicted the multi-select modal logic
    let baseFilter = `Announcement_id LIKE '%${annId}%' AND General_Availibility_ID LIKE '${gaId}'`;
    let searchFilter = searchTerm
      ? `AND (POSITION_NO LIKE '%${searchTerm}%' OR PositionTitle LIKE '%${searchTerm}%')` // --- FIXED search fields
      : "";
    let whereCondition = baseFilter; // --- FIXED: Added searchFilter back

    const payload = {
      tableName: "Eligibility-Summary",
      searchParams: "[]",
      columns: "*",
      location: "pmis-global",
      pageNumber,
      pageSize,
      sortData: "",
      additionalWhere: whereCondition,
      qID: 0,
    };
    setDataLoader(true);
    try {
      const response = await post(url, payload);
      setData(response?.results);
      setFilteredData(response?.results);
      setTotalItems(response?.rowCount);
      // Clear selection on new data load
      setSelectedState({});
    } catch (error) {
      console.error("Error fetching GA data:", error);
      ShowError(
        getFormalApiErrorMessage(
          error,
          "We are unable to load General Availability details at this time. Please try again shortly."
        )
      );
    } finally {
      setDataLoader(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (annId) {
        fetchData(currentPage, itemsPerPage, searchTerm);
      } else {
        setData([]);
        setFilteredData([]);
        setTotalItems(0);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [annId, searchTerm, currentPage, itemsPerPage, toggleApi]);

  // --- ADDED: Logic from your snippet ---
  const showAllApplicantsHandler = () => {
    const allSelectedElgIds = Object.keys(selectedState).filter(
      (item) => selectedState[item]
    );
    // 'data' is our 'dataState'
    const allSelectedElgData = data.filter((item) =>
      allSelectedElgIds.includes(item.GENERAL_AVAILIBILITY_ID)
    );
    setModalContent(allSelectedElgData);
    setAdditionalApplicantsCount(0);
    setSelectedApplicantIds({});
    toggleModal();
    fetchNotifiedApplicants();
  };

  const gaVacancyCount = Number(filteredData?.[0]?.Vacancy) || 0;
  const totalApplicantToInclude =
    gaVacancyCount + (Number(additionalApplicantsCount) || 0);

  // --- ADDED: Logic from your snippet ---
  const handleModalSubmit = async () => {
    if (selectedApplicantCount === 0) {
      ShowError("Please select at least one applicant to submit.");
      return;
    }
    try {
      setSubmitLoader(true);
      const payload = {
        announcement_id: annId,
        general_availability_id: gaId,
        vacancy: gaVacancyCount,
        // applicant_to_include: totalApplicantToInclude,
        applicant_to_include: 0,
        application_id: selectedApplicationIdsString,
      };
      // console.log(payload, "TEST DATA")
      // return;
      const response = await post(
        `${process.env.REACT_APP_API_DOTNET_URL}/GeneralAvaibility/GenerateCertList`,
        payload
      );

      if (response?.length > 0) {
        ShowSucess("Submitted succesfully");
        toggleModal();
        //   const gaIdForNav = modalContent[0].GENERAL_AVAILIBILITY_ID;

        if (setSubstepes) {
          history(`/certification-details/${annId}?gaId=${gaId}&certPid=${response[0].certificatioN_PID}`);
          setSubstepes(2)
          // history(`/certification-details/${annId}?gaId=${gaIdForNav}&certPid=`);
        }
      } else {
        ShowError(
          "We are unable to generate the certification list at this time. Please try again shortly."
        );
      }
    } catch (error) {
      console.log(error?.message ?? "Somethig went wrong");
      ShowError(
        getFormalApiErrorMessage(
          error,
          "We are unable to generate the certification list at this time. Please try again shortly."
        )
      );
    } finally {
      setSubmitLoader(false);
    }
  };

  // --- ADDED: Derived state for button ---
  const isShowAllApplicantsButtonVisible =
    !isEmpty(selectedState) &&
    Object.values(selectedState).some((v) => v === true);

  const pendingResponseCount =
    (filteredData?.[0]?.Total_Notify ?? 0) -
    (filteredData?.[0]?.Responded_Applicant ?? 0);
  const isGenerateCertListEnabled =
    pendingResponseCount > 0 || !!filteredData?.[0]?.Responded_Applicant;

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const displayedData = filteredData;

  return (
    <div style={{ padding: "0px" }}>
      {/* --- MODIFIED: Added Row and Button --- */}
      <Row className="d-flex align-items-center mb-3">
        <Col>
          <h3>Step 2: View GA</h3>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button
            disabled={!isGenerateCertListEnabled}
            color="primary"
            size="lg"
            className="ml-2"
            style={{ fontSize: "22px", padding: "14px 32px" }}
            onClick={showAllApplicantsHandler}
          >
            Generate Certification List
          </Button>
        </Col>
      </Row>
      <div style={{ overflowY: "auto", maxHeight: "60vh" }}>
        {dataLoader
          ? ["1", "2"].map((item) => (
            <Card sx={{ margin: "auto", mt: 5 }} key={item}>
              {" "}
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Skeleton variant="text" width="60%" height={40} />
                    <Skeleton variant="text" width="40%" />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Chip
                      label={<Skeleton variant="text" width={40} />}
                      color="default"
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="70%" />
                    <Skeleton variant="text" width="90%" />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="70%" />
                    <Skeleton variant="text" width="90%" />
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-around" mt={2}>
                  <Grid item>
                    <Chip
                      label={<Skeleton variant="text" width={40} />}
                      color="default"
                      size="small"
                    />
                  </Grid>
                  <Grid item>
                    <Chip
                      label={<Skeleton variant="text" width={40} />}
                      color="default"
                      size="small"
                    />
                  </Grid>
                  <Grid item>
                    <Chip
                      label={<Skeleton variant="text" width={40} />}
                      color="default"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))
          : displayedData.map((item, index) => (
            <AnnouncementDetail
              key={index}
              id={item.GENERAL_AVAILIBILITY_ID}
              cardData={item}
              setToggleApi={setToggleApi}
              // --- ADDED: Pass selection state down ---
              // Your AnnouncementDetail component must be updated
              // to accept these props and call setSelectedState.
              selectedState={selectedState}
              setSelectedState={setSelectedState}
            // e.g., onClick={() => setSelectedState(prev => ({...prev, [item.GENERAL_AVAILIBILITY_ID]: !prev[item.GENERAL_AVAILIBILITY_ID]}))}
            />
          ))}
      </div>
      {/* ... (Pagination and No Records Found JSX remains the same) ... */}
      {/* --- ADDED: Modal JSX --- */}
      <Modal isOpen={modal} toggle={toggleModal} size="xl">
        <ModalHeader toggle={toggleModal}>
          <Typography variant="h4">
            Create Certification List
          </Typography>
        </ModalHeader>
        <ModalBody>
          <div>
            <Typography variant="h6" sx={{ fontSize: "1.4rem" }}>
              Announcement : <strong>{annId}</strong>
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "1.4rem" }}>
              General Availability(s) : <strong>{gaId}</strong>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
              Select Applicants and then Click on "Generate" to Create Certification List for Position{" "}
              {displayedData ? (
                <b>
                  {displayedData?.[0]?.POSITION_NO}-
                  {displayedData?.[0]?.PositionTitle}
                </b>
              ) : (
                ""
              )}
              {/* --- MODIFIED: Assumed field name is POSITION_NO --- */}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, fontSize: "1.1rem" }}>
              Number of Vacancies: <strong>{gaVacancyCount}</strong>
            </Typography>
            {/* <TextField
              type="number"
              label="Additional Applicants to Notify"
              value={additionalApplicantsCount}
              onChange={(e) => setAdditionalApplicantsCount(e.target.value)}
              placeholder="Enter number of additional applicants"
              helperText="Added on top of the vacancy count to determine the certification list size."
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mt: 1 }}
              inputProps={{ min: 0 }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Total Applicants to Include:{" "}
              <strong>{totalApplicantToInclude}</strong> (Vacancy{" "}
              {gaVacancyCount} + Additional {Number(additionalApplicantsCount) || 0})
            </Typography> */}

            <Typography variant="body1" sx={{ mt: 2, fontWeight: 500, fontSize: "1.1rem" }}>
              Select the notified applicant(s) to include on the
              certification list:
            </Typography>
            {notifiedApplicantsLoading ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner size="sm" /> Loading notified applicants...
              </div>
            ) : notifiedApplicants.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No notified applicants found for this General Availability.
              </Typography>
            ) : (
              <div style={{ maxHeight: "650px", overflowY: "auto" }}>
                <Table bordered responsive style={{ fontSize: "16px" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>
                        <Checkbox
                          size="medium"
                          checked={
                            notifiedApplicants.length > 0 &&
                            notifiedApplicants.every(
                              (applicant) =>
                                selectedApplicantIds[applicant.APPLICATION_ID]
                            )
                          }
                          onChange={toggleAllApplicantsSelection}
                        />
                      </th>
                      <th>Applicant Name</th>
                      <th>Applicant Response</th>
                      <th>Application ID</th>
                      <th>Evaluation Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifiedApplicants.map((applicant, index) => {
                      let responseText = "No Response";
                      let responseColor = "#6c757d";
                      if (applicant.status === "Accept") {
                        responseText = "Available";
                        responseColor = "#28a745";
                      } else if (applicant.status === "Decline") {
                        responseText = "Unavailable";
                        responseColor = "#dc3545";
                      }
                      return (
                        <tr key={applicant.APPLICATION_ID || index}>
                          <td>
                            <Checkbox
                              size="medium"
                              checked={
                                !!selectedApplicantIds[applicant.APPLICATION_ID]
                              }
                              onChange={() =>
                                toggleApplicantSelection(applicant.APPLICATION_ID)
                              }
                            />
                          </td>
                          <td>{applicant.APPLICANT_NAME}</td>

                          <td style={{ textAlign: "center" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "0.5em 1em",
                                fontSize: "14px",
                                fontWeight: 700,
                                lineHeight: 1,
                                textAlign: "center",
                                whiteSpace: "nowrap",
                                borderRadius: "0.5rem",
                                color: "#fff",
                                backgroundColor: responseColor,
                              }}
                            >
                              {responseText}
                            </span>
                          </td>
                          <td>{applicant.APPLICATION_ID}</td>
                          <td>{applicant.EVALUATION_RATING}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleModalSubmit}
            disabled={submitLoader || selectedApplicantCount === 0}
            title={
              selectedApplicantCount === 0
                ? "Select at least one applicant to submit"
                : ""
            }
          >
            {submitLoader && <Spinner size="sm" />}
            Generate
          </Button>{" "}
          <Button
            color="secondary"
            onClick={toggleModal}
            disabled={submitLoader}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ViewGA;
